import { useState } from 'react';
import PropTypes from 'prop-types';
import { Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './ReviewCard.module.css';

/**
 * ReviewCard - Individual review card with expandable text
 */
export function ReviewCard({ review }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const shouldTruncate = review.text.length > 120;
  const displayText = isExpanded || !shouldTruncate 
    ? review.text 
    : review.text.slice(0, 120) + '...';
  
  return (
    <div className={styles.card}>
      <Stack direction="column" gap={3}>
        {/* Review Header */}
        <div className={styles.header}>
          <div className={styles.avatar}>
            {review.author.charAt(0).toUpperCase()}
          </div>
          
          <div className={styles.meta}>
            <Text weight="semibold" color="primary">
              {review.author}
            </Text>
            <Text size="sm" color="muted">
              {review.date}
            </Text>
          </div>
          
          <div className={styles.rating}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                size={14}
                className={i < review.rating ? styles.starFilled : styles.starEmpty}
                fill={i < review.rating ? 'currentColor' : 'none'}
              />
            ))}
          </div>
        </div>
        
        {/* Review Text */}
        <div className={styles.content}>
          <Text 
            color="secondary" 
            leading="relaxed"
            className={`${styles.text} ${isExpanded ? styles.expanded : ''}`}
          >
            {displayText}
          </Text>
          
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={styles.expandButton}
            >
              <Text size="sm" color="primary" weight="medium">
                {isExpanded ? 'Show Less' : 'Read More'}
              </Text>
              {isExpanded ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>
          )}
        </div>
      </Stack>
    </div>
  );
}

ReviewCard.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    author: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    date: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
};

export default ReviewCard;
